import { Injectable } from '@angular/core';
import { AmplifyService } from "aws-amplify-angular";
import * as AWS from "@aws-sdk/client-firehose-browser/Firehose";
import * as moment from "moment";

import { ConfigService } from "./config.service";
import { AuthService } from "../auth/auth.service";
import { ErrorHandlerService } from "./error-handler.service";

import { UserData } from "../interfaces/user-data";

const BUFFER_SIZE = 10;
const FLUSH_INTERVAL = 3 * 1000; // 3s

@Injectable({
    providedIn: 'root'
})
export class AnalyticsService {
    private gtag: any;
    private firehose: AWS.Firehose;
    private streamName: string;
    private credentials: any;
    private events: string[] = [];
    private timer: any = null;

    constructor(
        private amplify: AmplifyService,
        private config: ConfigService,
        private auth: AuthService,
        private errorServ: ErrorHandlerService,
    ) {
        this.streamName = this.config.get("amplify.AWSKinesis.streamName");
        this.init();
    }

    public async init(): Promise<boolean> {
        this.gtag = (window as any).gtag || (() => {
            console.warn("Google analytics failed to load");
            this.gtag = () => { /* noop */ };
        });

        this.setupFirehose(await this.getCredentials());
        this.setupTimer();

        this.auth.onSignout(async (): Promise<any> => {
            const success = await this.sendEvents();
            return (success ? Promise.resolve() : Promise.reject());
        });

        console.info("analytics initialized");
        return true;
    }

    public async closeout(): Promise<boolean> {
        await this.sendEvents(true);
        return true;
    }

    public createTrackingFn(page: string, map, prefix = ""): Function {
        if (prefix && !/:$/.test(prefix)) {
            prefix += ":";
        }

        return (action, data: any = {}, force: boolean = false): Promise<any> => {
            return this.track(page, prefix + action, {
                ...data,
                mapID: map._key,
            }, force);
        };
    }

    public async track(page: string, action: string, data: any = {}, force: boolean = false): Promise<any> {
        const location = window.location.href;
        const user = await this.getUserData();
        const source = page + "::" + action;
        const mapID = data.mapID ? data.mapID : null;
        const now = moment();
        delete data.mapID;
        const blob = JSON.stringify({
            source,
            page,
            action,
            user: user.sub,
            username: user.username,
            location,
            date: now.valueOf(),
            datestr: now.format(),
            mapID,
            data,
            userAgent: navigator.userAgent,
            PartitionKey: this.config.get("name"),
        }) + '\n';

        // google analytics
        this.gtag("event", source, blob);

        this.addEvent(blob);

        if (force) {
            this.sendEvents();
        }

        return blob;
    }

    private addEvent(blob: string): void {
        this.events.push(blob);
        if (this.events.length >= BUFFER_SIZE) {
            this.sendEvents();
        }
    }

    private async sendEvents(skipCreds: boolean = false): Promise<boolean> {
        // const loggedIn = await this.getSignedIn().toPromise();
        if (this.events.length === 0) {
            return true;
        }

        const events = [];
        const len = this.events.length;
        for (let i = 0; i < len && i < BUFFER_SIZE; i++) {
            events.push(this.events.shift());
        }

        const params = {
            DeliveryStreamName: this.streamName,
            Records: events.map(Data => ({ Data })),
        };

        if (!skipCreds) {
            this.setupFirehose(await this.getCredentials());
        }

        try {
            const resp = await this.firehose.putRecordBatch(params);
            if (resp.FailedPutCount > 0) {
                console.warn("failed to put some analytics records", resp);
                this.errorServ.logInfo({
                    "message": "Failed to put some analtyics records",
                    "count": resp.FailedPutCount,
                });
                return false;
            }
        } catch (e) {
            console.error("Error sending firehose stream!!!!", e);
            console.error("Error sending firehose stream!!!!", params);
            this.errorServ.logError(e);
            this.events.push(...events);
            return false;
        }

        return true;
    }

    private async getUserData(): Promise<UserData> {
        try {
            const u = await this.auth.getUser();
            return (u || {}) as UserData;
        } catch (e) {
            return {} as UserData;
        }
    }

    private setupFirehose(credentials: any): void {
        if (
            this.firehose &&
            this.credentials &&
            this.credentials.sessionToken === credentials.sessionToken &&
            this.credentials.identityId === credentials.identityId
        ) {
            // NOTHING to
            // console.log("no change to credentials...");
        } else {
            // console.log("setup credentials");
            this.credentials = credentials;
            const params = {
                region: this.config.get("amplify.AWSKinesis.region"),
                credentials,
            };
            this.firehose = new AWS.Firehose(params);
        }
    }

    private setupTimer(): void {
        if (this.timer) {
			clearInterval(this.timer);
		}

		this.timer = setInterval(() => {
            // console.log("send records timer");
            if (this.events.length >= BUFFER_SIZE) {
                this.sendEvents();
            }
		}, FLUSH_INTERVAL);
    }

    private async getCredentials(): Promise<any> {
        // return await this.amplify.auth().currentUserCredentials();
        return await this.amplify.auth().currentCredentials();
    }
}
